import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useDebounceSearchTextFn } from '../../../common/hooks/use-debounce.hook';
import { Modal } from '../../../shared/components/Modal/Modal';
import { EditFailedGift } from "./EditFailedGift";
import { SidebarLayoutHeader } from '../../../shared/components/SidebarLayoutHeader';
import { Pagination } from '../../shared/Pagination';
import { formatDateForTable } from '../../../common/utils/date.util';
import { useLoaderStore } from '../../../stores/loader.store';
import { useSetBreadcrumbs } from '../../../shared/hooks/use-set-breadcrumbs.hook';
import { ReleaseModerationFailedGift } from "./ReleaseModerationFailedGift";
import giftService from '../../../Services/gift.service';
import { highlightThankyouMessageMatchInRed } from '../../../common/utils/text-highlight';
import { useSearchParams } from 'react-router-dom';
import { createSearchParamsFromListState, getListStateFromSearchParams } from '../../../utils/list-state.util';
import { ThankYouListFilters } from '../../ThankYou/ThankYouListFilters';
import { DEFAULT_PAGE_SIZE } from '../../../common/constants/pagination.constant';

const MODERATION_FAILED_MESSAGE_ACTION = Object.freeze({
    DELETE: "DELETE",
    EDIT: "EDIT",
    RELEASE: "RELEASE"
});

const singleFilters = ['organizationId', 'platformFlow', 'dateFilterType', 'dateRangeStart', 'dateRangeEnd'];
const multiFilters = ['teamIds'];

/**
 * @typedef SelectedRow
 * @property {string} moderationFailedMessage
 * @property {keyof typeof MODERATION_FAILED_MESSAGE_ACTION} action
 */

export function ModerationFailedGiftList() {
    const setShowLoader = useLoaderStore((state) => state.setShowLoader);
    const [draftGifts, setDraftGifts] = useState([]);
    const [resultsCount, setResultsCount] = useState(0);
    const [filterData, setFilterData] = useState('notReleased');
    const [isFirstRender, setIsFirstRender] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const [showFilters, setShowFilters] = useState(false);

    const state = useMemo(() => getThankYouListStateFromSearchParams(searchParams), [searchParams]);
    const filters = state.filters;
    const pagination = { pageNumber: state.pageNumber, pageSize: DEFAULT_PAGE_SIZE };
    const [searchText, setSearchText] = useState(state.searchText);

    /** @type {[SelectedRow, React.Dispatch<SelectedRow>]} */
    const [selectedRow, setSelectedRow] = useState(null);

    useSetBreadcrumbs([{ label: 'Moderation Failed Messages', path: '' }]);

    useDebounceSearchTextFn(searchText, (debouncedSearchText) => {
        if (debouncedSearchText !== state.searchText) {
            setSearchParams(createThankYouListSearchParams(filters, debouncedSearchText, 1));
        }
    });

    useEffect(() => {
        getGifts();
        setIsFirstRender(false);
    }, [filterData, searchParams]);

    async function getGifts() {
        setShowLoader(true);

        try {
            const res = await giftService.getModerationFailedGifts({
                search: state.searchText ? state.searchText.trim() : '',
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize,
                status: filterData,
                platformFlow: filters.platformFlow,
                organizationIds: filters.organizationId ? [filters.organizationId] : [],
                teamIds: filters.teamIds,
                dateRangeStart: filters.dateFilterType === "range" ? filters.dateRangeStart : undefined,
                dateRangeEnd: filters.dateFilterType === "range" ? filters.dateRangeEnd : undefined,

            });

            if (res.status === 200) {
                let messages = res.data.results;

                for (let i = 0; i < messages.length; i++) {
                    let item = messages[i];
                    if (item.failedReason) {
                        let highlightData = highlightThankyouMessageMatchInRed(JSON.parse(JSON.stringify(item.message)), JSON.parse(JSON.stringify(item.createdUserData)), item.failedReason);
                        item.messageHighlighted = highlightData.message;
                        item.createdUserDataHighlighted = highlightData.contact;
                    }
                    else {
                        item.messageHighlighted = item.message;
                        item.createdUserDataHighlighted = item.createdUserData;
                    }
                }

                setDraftGifts(messages);
                setResultsCount(res.data.count);

                return;
            }

            toast.error("Couldn't get thank yous");
        } catch (err) {
            toast.error("Couldn't get thank yous");
        } finally {
            setShowLoader(false);
        }
    }

    function onPaginationChange(pageNumber) {
        setSearchParams(createThankYouListSearchParams(filters, state.searchText, pageNumber));
    }

    function createThankYouListSearchParams(filters, searchText, pageNumber) {
        return createSearchParamsFromListState(filters, searchText, pageNumber, singleFilters, multiFilters);
    }

    function getThankYouListStateFromSearchParams(searchParams) {
        return getListStateFromSearchParams(searchParams, singleFilters, multiFilters);
    }

    const handleApplyFilters = (newFilters) => {
        setShowFilters(false);
        setSearchParams(createThankYouListSearchParams(newFilters, state.searchText));
    }

    const extractReason = (draftGift) => {
        if (draftGift?.failedReason) {
            const reasons = draftGift?.failedReason?.map(failedReason => {
                return failedReason?.reasons?.map(reason => reason.reason)[0] ?? ""
            }) ?? "";
            return reasons.join(", ")
        }
    }

    return (
        <>
            <main className="page-content bg-light">
                <SidebarLayoutHeader title="Failed Gift List" />

                <div className="container-fluid">
                    <div className="layout-specing">
                        <div className="card border-0 shadow rounded">
                            <div className='d-flex justify-content-end align-items-center p-2'>
                                <div className="mr-2">
                                    <i className="fi fi-rr-search cstm-search-ro"></i>
                                    <input
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        type="text"
                                        className="cstm-input-seacrh"
                                        placeholder="Search"
                                    />
                                </div>
                                <button type='button' className='custom-filter-icon-btn mr-2' onClick={() => setShowFilters(true)}>
                                    <i className="fi fi-rr-settings-sliders"></i>
                                </button>
                                <div className="">
                                    <button
                                        className='cstm-btn lh-base ml-2'
                                        onClick={(e) => setFilterData('released')}
                                    >
                                        Released Gifts
                                    </button>
                                </div>
                            </div>

                            <div className="col-md-12 col-lg-12">
                                <table className="table mb-0 table-center">
                                    <thead>
                                        <tr className="border-top">
                                            <th className="border-bottom w-4 cstm-userheading">Message</th>
                                            <th className="border-bottom w-4 cstm-userheading">From</th>
                                            <th className="border-bottom w-4 cstm-userheading">To</th>
                                            <th className="border-bottom w-4 cstm-userheading">Team</th>
                                            <th className="border-bottom w-4 cstm-userheading">Reason</th>
                                            <th className="border-bottom w-4 cstm-userheading">Created Date</th>
                                            <th className="border-bottom w-4 cstm-userheading">Actions</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {draftGifts?.map((gift) => {
                                            return (
                                                <tr
                                                    key={gift._id}
                                                    className="cstm-Tabledesign cstm-usertable-design"
                                                >
                                                    <td><p style={{ color: "black" }} dangerouslySetInnerHTML={{ __html: gift.messageHighlighted }} /></td>
                                                    <td>
                                                        <div>{gift.createdUserData?.firstName}</div>
                                                        <div><p style={{ color: "black" }} dangerouslySetInnerHTML={{ __html: gift.createdUserDataHighlighted?.email }} /></div>
                                                        <div>{gift.createdUserData?.phoneNumber}</div>
                                                    </td>
                                                    <td>
                                                        <div>{gift.users ? gift.users[0]?.firstName : null}</div>
                                                        <div>{gift.users ? gift.users[0]?.email : null}</div>
                                                        <div>{gift.users ? gift.users[0]?.phoneNumber : null}</div>
                                                    </td>
                                                    <td>
                                                        <div>{gift.teams && gift.teams[0] ? gift.teams[0]?.name : 'N/A'}</div>
                                                    </td>
                                                    <td>
                                                        <div>{gift.failedReason ? extractReason(gift) : 'N/A'}</div>
                                                    </td>

                                                    <td>{gift.createdAt ? formatDateForTable(gift.createdAt) : null}</td>
                                                    <td>
                                                        <div>
                                                            <button
                                                                to={""}
                                                                onClick={() => setSelectedRow({ moderationFailedMessage: gift, action: MODERATION_FAILED_MESSAGE_ACTION.EDIT })}
                                                                class="cstm-delete mrn-rt"
                                                            >
                                                                <i className="fi fi-rr-pencil"></i>
                                                            </button>
                                                            {gift.isReleased ? 'Released' :
                                                                <button
                                                                    type='button'
                                                                    className="cstm-eye"
                                                                    onClick={() => setSelectedRow({ moderationFailedMessage: gift, action: MODERATION_FAILED_MESSAGE_ACTION.RELEASE })}
                                                                    class="cstm-delete mrn-rt"
                                                                >
                                                                    <i className="fi fi-rr-cloud-upload"></i>

                                                                </button>}
                                                        </div>
                                                    </td>

                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                {!draftGifts?.length && (
                                    <div className="NoRecord-cstm">No records found!</div>
                                )}
                            </div>
                        </div>

                        <Pagination
                            totalRows={resultsCount}
                            currentPage={pagination.pageNumber}
                            rowsPerPage={pagination.pageSize}
                            onPaginationChange={onPaginationChange}
                        />
                    </div>
                </div>
            </main>

            {(selectedRow && selectedRow.action === MODERATION_FAILED_MESSAGE_ACTION.EDIT) && (
                <Modal
                    show={true}
                    onClose={() => setSelectedRow(null)}
                >
                    <EditFailedGift
                        moderationFailedGift={selectedRow.moderationFailedMessage}
                        onEditSuccess={() => {
                            setSelectedRow(null);
                            getGifts(pagination.pageNumber, pagination.pageSize);
                        }}
                    />
                </Modal>
            )}

            {(selectedRow && selectedRow.action === MODERATION_FAILED_MESSAGE_ACTION.RELEASE) && (
                <Modal
                    show={true}
                    onClose={() => setSelectedRow(null)}
                >
                    <ReleaseModerationFailedGift
                        moderationFailedGift={selectedRow.moderationFailedMessage}
                        onReleaseSuccess={() => {
                            setSelectedRow(null);
                        }}
                    />
                </Modal>
            )}

            <Modal
                show={showFilters}
                onClose={() => setShowFilters(false)}
            >
                <ThankYouListFilters
                    filters={filters}
                    onApplyFilters={handleApplyFilters}
                />
            </Modal>
        </>
    );
}